import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import axios, { AxiosResponse } from "axios";
import { client } from "../services/client";
import Logger from "../services/Logger";


interface UserContextState {
    userContext: UserContext
    status: string // to handle loading
    errorMessage: string
    userPrivileges: RolePrivilege[]
}

interface UserContext {
    id: string,
    firstName: string,
    lastName: string,
    email: string,
    pageSize: number,
    objectId: string,
    isActive: boolean,
    agencyGroupId: string,
    defaultDocumateGroup?: string
}

interface RolePrivilege {
    appId: string,
    featureId: string,
    featureName: string,
    allowAccess: boolean,
    elementPrivileges: ElementPrivilege[]
}

interface ElementPrivilege {
    featureId: string,
    elementName: string,
    subject: string,
    action: string,
    access: boolean,
    insert: boolean,
    update: boolean,
    delete: boolean
}

const initialState: UserContextState = {
    userContext: {
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        pageSize: 10,
        objectId: '',
        isActive: false
    },
    status: 'idle',
    errorMessage: '',
    userPrivileges: []
}

export const fetchUserDetails = createAsyncThunk('userContext/fetchUser', async (_, { rejectWithValue }) => {
    try {
        client.setBaseURLForService("security");
        const response: AxiosResponse<any> = await client.get("/api/RolePrivilege/GetUserContextOnLogin");
        return response.data;
    } catch (error: any) {
        if (error?.status === 401) {
            const message = error?.data || error?.statusText || 'Unauthorized';
            Logger.logError('fetchUserDetails 401: ' + message, error);
            return rejectWithValue(message);
        }
        Logger.logError('fetchUserDetails failed', error);
        throw error;
    }
})

const userContextSlice = createSlice({
    name: 'userContext',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
        .addCase(fetchUserDetails.pending, (state) => {
            state.status = 'loading';
        })
        .addCase(fetchUserDetails.fulfilled, (state, action) => {
            state.status = 'succeeded';
            if(action.payload) {
                state.userContext = {
                    id: action.payload.userContextDto.id,
                    firstName: action.payload.userContextDto.firstName,
                    lastName: action.payload.userContextDto.lastName,
                    email: action.payload.userContextDto.email,
                    pageSize: action.payload.userContextDto.pageSize,
                    objectId: action.payload.userContextDto.objectId,
                    isActive: action.payload.userContextDto.isActive,
                    agencyGroupId: action.payload.userContextDto.agencyGroupId,
                    defaultDocumateGroup: action.payload.userContextDto.defaultDocumateGroup              
                }
                state.userPrivileges = action.payload.userPrivilegesDtos.map(privilege => {
                    return {
                        ...privilege,
                    };
                });
            }
        })
        .addCase(fetchUserDetails.rejected, (state, action) => {
            if (action.payload) {
                // 401 Unauthorized - show unauthorized page
                state.status = 'failed';
                state.errorMessage = action.payload as string;
            } else {
                // Other errors (500, network, etc.) - reset to idle so user can retry
                state.status = 'idle';
            }
        })
    },
})

export default userContextSlice.reducer;